import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { authAPI, notificationAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Settings as SettingsIcon,
  User,
  Mail,
  MessageSquare,
  Bell,
  Loader2,
  Save,
  CheckCircle2,
  Lock,
  Sun,
  Moon,
  Monitor,
  Languages
} from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const { user, isAdmin, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const t = useTranslation(user);

  // Profile
  const [editName, setEditName] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('vi');
  const [savingProfile, setSavingProfile] = useState(false);

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // Notifications
  const [notificationEmail, setNotificationEmail] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [savingNotif, setSavingNotif] = useState(false);

  useEffect(() => {
    if (user) {
      setEditName(user.name || '');
      setNotificationEmail(user.notification_email || '');
      setTelegramChatId(user.telegram_chat_id || '');
      setSelectedLanguage(user.language || 'vi');
    }
  }, [user]);

  const handleSaveProfile = async () => {
    if (!editName.trim()) {
      toast.error('Tên không được để trống');
      return;
    }
    setSavingProfile(true);
    try {
      await authAPI.updateProfile({ name: editName.trim(), language: selectedLanguage });
      await refreshUser();
      toast.success('Đã cập nhật thông tin!');
      // Force reload to apply language changes globally
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cập nhật thất bại');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error('Vui lòng nhập mật khẩu hiện tại');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error('Mật khẩu mới phải ít nhất 6 ký tự');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Mật khẩu xác nhận không khớp');
      return;
    }
    setSavingPassword(true);
    try {
      await authAPI.updateProfile({ current_password: currentPassword, new_password: newPassword });
      toast.success('Đã đổi mật khẩu thành công!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Đổi mật khẩu thất bại');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotif(true);
    try {
      await notificationAPI.updateSettings({
        notification_email: notificationEmail,
        telegram_chat_id: telegramChatId,
      });
      await refreshUser();
      toast.success('Đã cập nhật cài đặt thông báo!');
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setSavingNotif(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="animate-fadeIn">
        <h1 className="text-2xl font-bold text-[#1C1E21] flex items-center gap-2">
          <SettingsIcon className="text-[#1877F2]" />
          {t('settings')}
        </h1>
        <p className="text-[#606770] mt-1">
          Quản lý thông tin tài khoản và cài đặt thông báo
        </p>
      </div>

      <Tabs defaultValue="profile" className="animate-fadeIn stagger-1">
        <TabsList className="bg-[#F0F2F5] p-1 w-full flex-wrap h-auto justify-start gap-1">
          <TabsTrigger value="profile" className="data-[state=active]:bg-white" data-testid="profile-tab">
            <User size={16} className="mr-2" />
            {t('profile')}
          </TabsTrigger>
          <TabsTrigger value="password" className="data-[state=active]:bg-white" data-testid="password-tab">
            <Lock size={16} className="mr-2" />
            Mật khẩu
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-white" data-testid="notifications-tab">
            <Bell size={16} className="mr-2" />
            Thông báo
          </TabsTrigger>
          <TabsTrigger value="appearance" className="data-[state=active]:bg-white" data-testid="appearance-tab">
            <Sun size={16} className="mr-2" />
            {t('appearance')}
          </TabsTrigger>
          <TabsTrigger value="language" className="data-[state=active]:bg-white" data-testid="language-tab">
            <Languages size={16} className="mr-2" />
            {t('language')}
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6">
          <Card data-testid="profile-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Thông tin cá nhân</CardTitle>
              <CardDescription>Chỉnh sửa thông tin tài khoản của bạn</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Họ và tên</Label>
                  <Input
                    id="edit-name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Nhập họ và tên"
                    data-testid="edit-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={user?.email || ''} disabled className="bg-[#F7F8FA]" />
                  <p className="text-xs text-[#8C939D]">Email không thể thay đổi</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vai trò</Label>
                  <Input 
                    value={user?.role === 'admin' ? 'Quản trị viên' : 'Người dùng'} 
                    disabled 
                    className="bg-[#F7F8FA]" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ngày tạo</Label>
                  <Input 
                    value={user?.created_at ? new Date(user.created_at).toLocaleDateString('vi-VN') : ''} 
                    disabled 
                    className="bg-[#F7F8FA]" 
                  />
                </div>
              </div>

              <div className="p-4 bg-[#E7F3FF] rounded-xl">
                <div className="flex items-center gap-2 text-[#1877F2] font-semibold">
                  <CheckCircle2 size={18} />
                  Tài khoản đang hoạt động
                </div>
                <p className="text-sm text-[#606770] mt-1">
                  Trạng thái: <strong>{isAdmin ? 'Quản trị viên' : (user?.subscription_type === 'monthly' ? 'VIP Member' : 'Thành viên')}</strong>
                </p>
              </div>

              <Button
                onClick={handleSaveProfile}
                className="w-full bg-[#1877F2] hover:bg-[#166FE5]"
                disabled={savingProfile || editName === user?.name}
                data-testid="save-profile-btn"
              >
                {savingProfile ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" />Đang lưu...</>
                ) : (
                  <><Save size={16} className="mr-2" />Lưu thông tin</>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Password Tab */}
        <TabsContent value="password" className="mt-6">
          <Card data-testid="password-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Đổi mật khẩu</CardTitle>
              <CardDescription>Cập nhật mật khẩu đăng nhập</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Mật khẩu hiện tại</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Nhập mật khẩu hiện tại"
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">Mật khẩu mới</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nhập mật khẩu mới (tối thiểu 6 ký tự)"
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Xác nhận mật khẩu mới</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Nhập lại mật khẩu mới"
                  data-testid="confirm-password-input"
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-[#E41E3F]">Mật khẩu xác nhận không khớp</p>
                )}
              </div>

              <Button
                onClick={handleChangePassword}
                className="w-full bg-[#1877F2] hover:bg-[#166FE5]"
                disabled={savingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword}
                data-testid="change-password-btn"
              >
                {savingPassword ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" />Đang đổi...</>
                ) : (
                  <><Lock size={16} className="mr-2" />Đổi mật khẩu</>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-6">
          <Card data-testid="notifications-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Cài đặt thông báo</CardTitle>
              <CardDescription>Cấu hình kênh nhận thông báo về các link Hotface</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 border rounded-xl space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E7F3FF] flex items-center justify-center">
                    <Mail size={20} className="text-[#1877F2]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1C1E21]">Email</h3>
                    <p className="text-sm text-[#606770]">Nhận thông báo qua email</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notificationEmail">Email nhận thông báo</Label>
                  <Input
                    id="notificationEmail"
                    type="email"
                    placeholder="email@example.com"
                    value={notificationEmail}
                    onChange={(e) => setNotificationEmail(e.target.value)}
                    data-testid="notification-email-input"
                  />
                </div>
              </div>

              <div className="p-4 border rounded-xl space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E7F3FF] flex items-center justify-center">
                    <MessageSquare size={20} className="text-[#1877F2]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1C1E21]">Telegram</h3>
                    <p className="text-sm text-[#606770]">Nhận thông báo qua Telegram Bot</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telegramChatId">Telegram Chat ID</Label>
                  <Input
                    id="telegramChatId"
                    placeholder="123456789"
                    value={telegramChatId}
                    onChange={(e) => setTelegramChatId(e.target.value)}
                    data-testid="telegram-chat-id-input"
                  />
                </div>
              </div>

              <Button
                onClick={handleSaveNotifications}
                className="w-full bg-[#1877F2] hover:bg-[#166FE5]"
                disabled={savingNotif}
                data-testid="save-notifications-btn"
              >
                {savingNotif ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" />Đang lưu...</>
                ) : (
                  <><Save size={16} className="mr-2" />Lưu cài đặt</>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance" className="mt-6">
          <Card data-testid="appearance-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Giao diện</CardTitle>
              <CardDescription>Chọn chế độ hiển thị phù hợp với bạn</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {/* Light */}
                <button
                  onClick={() => setTheme('light')}
                  className={`flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all ${
                    theme === 'light'
                      ? 'border-[#1877F2] bg-[#E7F3FF]'
                      : 'border-[#E4E6EB] hover:border-[#1877F2]/40 bg-white'
                  }`}
                  data-testid="theme-light-btn"
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    theme === 'light' ? 'bg-[#1877F2] text-white' : 'bg-[#F0F2F5] text-[#606770]'
                  }`}>
                    <Sun size={22} />
                  </div>
                  <span className={`text-sm font-medium ${
                    theme === 'light' ? 'text-[#1877F2]' : 'text-[#606770]'
                  }`}>Sáng</span>
                  {theme === 'light' && (
                    <CheckCircle2 size={16} className="text-[#1877F2]" />
                  )}
                </button>

                {/* Dark */}
                <button
                  onClick={() => setTheme('dark')}
                  className={`flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all ${
                    theme === 'dark'
                      ? 'border-[#1877F2] bg-[#E7F3FF]'
                      : 'border-[#E4E6EB] hover:border-[#1877F2]/40 bg-white'
                  }`}
                  data-testid="theme-dark-btn"
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    theme === 'dark' ? 'bg-[#1877F2] text-white' : 'bg-[#F0F2F5] text-[#606770]'
                  }`}>
                    <Moon size={22} />
                  </div>
                  <span className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-[#1877F2]' : 'text-[#606770]'
                  }`}>Tối</span>
                  {theme === 'dark' && (
                    <CheckCircle2 size={16} className="text-[#1877F2]" />
                  )}
                </button>

                {/* System */}
                <button
                  onClick={() => {
                    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                    setTheme(prefersDark ? 'dark' : 'light');
                    toast.success('Đã áp dụng theo hệ thống');
                  }}
                  className={`flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all border-[#E4E6EB] hover:border-[#1877F2]/40 bg-white`}
                  data-testid="theme-system-btn"
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#F0F2F5] text-[#606770]">
                    <Monitor size={22} />
                  </div>
                  <span className="text-sm font-medium text-[#606770]">Hệ thống</span>
                </button>
              </div>

              <p className="text-xs text-[#8C939D] mt-4">
                Cài đặt được lưu tự động và áp dụng ngay lập tức.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language" className="mt-6">
          <Card data-testid="language-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">{t('language')}</CardTitle>
              <CardDescription>Chọn ngôn ngữ hiển thị cho giao diện</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Vietnamese */}
                <button
                  onClick={() => setSelectedLanguage('vi')}
                  className={`flex items-center gap-4 p-5 rounded-xl border-2 transition-all ${
                    selectedLanguage === 'vi'
                      ? 'border-[#1877F2] bg-[#E7F3FF]'
                      : 'border-[#E4E6EB] hover:border-[#1877F2]/40 bg-white'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-2xl">
                    🇻🇳
                  </div>
                  <div className="text-left flex-1">
                    <p className={`font-bold ${selectedLanguage === 'vi' ? 'text-[#1877F2]' : 'text-[#1C1E21]'}`}>Tiếng Việt</p>
                    <p className="text-xs text-[#606770]">Mặc định hệ thống</p>
                  </div>
                  {selectedLanguage === 'vi' && <CheckCircle2 size={18} className="text-[#1877F2]" />}
                </button>

                {/* English */}
                <button
                  onClick={() => setSelectedLanguage('en')}
                  className={`flex items-center gap-4 p-5 rounded-xl border-2 transition-all ${
                    selectedLanguage === 'en'
                      ? 'border-[#1877F2] bg-[#E7F3FF]'
                      : 'border-[#E4E6EB] hover:border-[#1877F2]/40 bg-white'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-2xl">
                    🇺🇸
                  </div>
                  <div className="text-left flex-1">
                    <p className={`font-bold ${selectedLanguage === 'en' ? 'text-[#1877F2]' : 'text-[#1C1E21]'}`}>English</p>
                    <p className="text-xs text-[#606770]">Global language</p>
                  </div>
                  {selectedLanguage === 'en' && <CheckCircle2 size={18} className="text-[#1877F2]" />}
                </button>
              </div>

              <div className="mt-8 flex justify-end">
                <Button
                  onClick={handleSaveProfile}
                  className="bg-[#1877F2] hover:bg-[#166FE5] px-8"
                  disabled={savingProfile || (selectedLanguage === user?.language && editName === user?.name)}
                >
                  {savingProfile ? (
                    <><Loader2 size={16} className="mr-2 animate-spin" />{t('saving')}</>
                  ) : (
                    <><Save size={16} className="mr-2" />{t('update_language')}</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
